import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Home, Building2, Hammer, Zap, ArrowRight, Package, Type, Wrench, Grid3x3,
  CheckCircle, Phone, MessageCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getServices } from '../data/api';

const iconMap = {
  Home: Home,
  Building2: Building2,
  Hammer: Hammer,
  Lightbulb: Zap,
  Package: Package,
  Zap: Zap,
  Type: Type,
  Wrench: Wrench,
  Grid3x3: Grid3x3
};

const ServicesProfessional = () => {
  const [services, setServices] = useState([]);
  const [activeService, setActiveService] = useState(null);

  useEffect(() => {
    getServices().then(data => {
      setServices(data);
      if (data.length > 0) setActiveService(data[0]);
    }).catch(() => {});
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Service categories with colors
  const serviceColors = {
    '1': { bg: 'bg-blue-500', light: 'bg-blue-50', text: 'text-blue-600' },
    '2': { bg: 'bg-indigo-500', light: 'bg-indigo-50', text: 'text-indigo-600' },
    '3': { bg: 'bg-amber-500', light: 'bg-amber-50', text: 'text-amber-600' },
    '4': { bg: 'bg-orange-500', light: 'bg-orange-50', text: 'text-orange-600' },
    '5': { bg: 'bg-pink-500', light: 'bg-pink-50', text: 'text-pink-600' },
    '6': { bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-emerald-600' },
    '7': { bg: 'bg-cyan-500', light: 'bg-cyan-50', text: 'text-cyan-600' },
    '8': { bg: 'bg-violet-500', light: 'bg-violet-50', text: 'text-violet-600' },
    '9': { bg: 'bg-rose-500', light: 'bg-rose-50', text: 'text-rose-600' },
  };

  const IconComponent = iconMap[activeService?.icon] || Home;
  const colors = serviceColors[activeService?.id] || serviceColors['1'];

  if (!activeService) return null;

  return (
    <section id="services" className="py-12 sm:py-16 lg:py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header - Compact on mobile */}
        <div className="text-center mb-6 sm:mb-8">
          <Badge className="mb-2 sm:mb-4 bg-blue-600 text-white px-3 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm hover:bg-blue-600">
            What We Offer
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
            Our Services
          </h2>
          <p className="text-sm sm:text-base text-gray-400 hidden sm:block">
            Comprehensive interior design & renovation solutions
          </p>
        </div>

        {/* Horizontal Scrollable Service Tabs */}
        <div className="flex gap-2 mb-4 sm:mb-6 overflow-x-auto pb-2 scrollbar-hide">
          {services.map((service) => {
            const ServiceIcon = iconMap[service.icon] || Home;
            const isActive = activeService?.id === service.id;
            const svcColors = serviceColors[service.id] || serviceColors['1'];
            
            return (
              <button
                key={service.id}
                onClick={() => setActiveService(service)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                  isActive 
                    ? `${svcColors.bg} text-white shadow-lg` 
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <ServiceIcon className="w-4 h-4" />
                <span className="text-xs sm:text-sm font-medium">{service.title}</span>
              </button>
            );
          })}
        </div>

        {/* Content Card - Compact for mobile */}
        {activeService && (
          <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-lg">
            {/* Service Header */}
            <div className={`${colors.bg} p-4 sm:p-6`}>
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <IconComponent className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white truncate">
                    {activeService.title}
                  </h3>
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-4 sm:p-6">
              {/* Description */}
              <p className="text-sm sm:text-base text-gray-600 mb-4 leading-relaxed line-clamp-3 sm:line-clamp-none">
                {activeService.description}
              </p>

              {/* Features - Horizontal scroll on mobile */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">What's Included</h4>
                <div className="flex gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:overflow-visible">
                  {activeService.features.slice(0, 6).map((feature) => (
                    <div 
                      key={feature} 
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full whitespace-nowrap flex-shrink-0 ${colors.light}`}
                    >
                      <CheckCircle className={`w-3.5 h-3.5 flex-shrink-0 ${colors.text}`} />
                      <span className="text-xs sm:text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Buttons - Full width on mobile */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-3 border-t border-gray-100">
                <Button
                  onClick={scrollToContact}
                  className={`${colors.bg} hover:opacity-90 text-white flex-1 py-2.5 sm:py-3 text-sm`}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Request Quote
                </Button>
                <Link
                  to={`/services/${activeService.id}`}
                  className="flex-1"
                  data-testid="service-learn-more-link"
                >
                  <Button
                    variant="outline"
                    className="border-gray-300 hover:border-blue-600 hover:text-blue-600 w-full py-2.5 sm:py-3 text-sm"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Bottom Stats Banner - Compact */}
        <div className="mt-6 sm:mt-8 grid grid-cols-4 gap-2 sm:gap-4">
          <div className="text-center p-2 sm:p-4 bg-gray-800 rounded-lg sm:rounded-xl">
            <div className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-400 mb-0.5">9+</div>
            <div className="text-[10px] sm:text-xs text-gray-400">Services</div>
          </div>
          <div className="text-center p-2 sm:p-4 bg-gray-800 rounded-lg sm:rounded-xl">
            <div className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-400 mb-0.5">150+</div>
            <div className="text-[10px] sm:text-xs text-gray-400">Projects</div>
          </div>
          <div className="text-center p-2 sm:p-4 bg-gray-800 rounded-lg sm:rounded-xl">
            <div className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-400 mb-0.5">38+</div>
            <div className="text-[10px] sm:text-xs text-gray-400">Years</div>
          </div>
          <div className="text-center p-2 sm:p-4 bg-gray-800 rounded-lg sm:rounded-xl">
            <div className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-400 mb-0.5">100%</div>
            <div className="text-[10px] sm:text-xs text-gray-400">Quality</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesProfessional;
